/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useEffect, useRef, useState } from "react";
import Container from "@/src/components/Container/Container";
import AndreasQuaukeImage from "@/src/assets/img/employees/Andreas_Quauke.webp";
import AngelaFeilImage from "@/src/assets/img/employees/Angela_Feil.webp";
import AnnaBeitenImage from "@/src/assets/img/employees/Anna_Beiten.webp";
import ChristopherKrauseImage from "@/src/assets/img/employees/Christopher_Krause.webp";
import DarioBorningImage from "@/src/assets/img/employees/Dario_Borning.webp";
import ElenorGrubImage from "@/src/assets/img/employees/Elenor_Grub.webp";
import FrankRiedelImage from "@/src/assets/img/employees/Frank_Riedel.webp";
import FrederikSchierImage from "@/src/assets/img/employees/Frederik_Schier.webp";
import HaraldHoevelerImage from "@/src/assets/img/employees/Harald_Hoeveler.webp";
import KatharinaPitzenImage from "@/src/assets/img/employees/Katharina_Pitzen.webp";
import KristinaBeyerImage from "@/src/assets/img/employees/Kristina_Beyer.webp";
import LaraVonKolontajImage from "@/src/assets/img/employees/Lara_von_Kolontaj.webp";
import MajaVonKutzschenbachImage from "@/src/assets/img/employees/Maja_von_Kutzschenbach.webp";
import MelanieTreudeImage from "@/src/assets/img/employees/Melanie_Treude.webp";
import MeralCitakImage from "@/src/assets/img/employees/Meral_Citak.webp";
import MichaelBickImage from "@/src/assets/img/employees/Michael_Bick.webp";
import SarahLongerichImage from "@/src/assets/img/employees/Sarah_Longerich.webp";
import SebastianPleissImage from "@/src/assets/img/employees/Sebastian_Pleiss.webp";
import SonjaHartmannImage from "@/src/assets/img/employees/Sonja_Hartmann.webp";
import WingImage from "@/src/assets/img/jobs/wing.svg";
import Typography from "@mui/material/Typography";
import PictureTeaserCard from "@/src/components/Cards/PictureTeaserCard/PictureTeaserCard";
import teamSectionStyle from "@/src/components/Sections/Generic/TeamSection/teamSectionStyle";
import { Box, Button, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import Section from "@/src/components/Section/Section";

type TeamSectionProps = {
  headline?: string;
  subHeadline?: string;
  isSubsection?: boolean;
  showHeadings?: boolean;
};

type EmployeeItem = {
  title: string;
  subTitle?: string;
  image: string;
  isLightVariant?: boolean;
};

const TeamSection = ({ headline = "", subHeadline, isSubsection = false, showHeadings = true }: TeamSectionProps) => {
  const [showAllEmployees, setShowAllEmployees] = useState(false);
  const toggleButtonRef = useRef<HTMLDivElement | null>(null);

  const employees: EmployeeItem[] = [
    {
      title: "Andreas Quauke",
      image: AndreasQuaukeImage,
    },
    {
      title: "Angela Feil",
      image: AngelaFeilImage,
    },
    {
      title: "Anna Beiten",
      image: AnnaBeitenImage,
    },
    {
      title: "Christopher Krause",
      image: ChristopherKrauseImage,
    },
    {
      title: "Dario Borning",
      image: DarioBorningImage,
    },
    {
      title: "Elenor Grub",
      image: ElenorGrubImage,
    },
    {
      title: "Frank Riedel",
      image: FrankRiedelImage,
    },
    {
      title: "Frederik Schier",
      image: FrederikSchierImage,
    },
    {
      title: "Harald Höveler",
      image: HaraldHoevelerImage,
    },
    {
      title: "Katharina Pitzen",
      image: KatharinaPitzenImage,
    },
    {
      title: "Kristina Beyer",
      image: KristinaBeyerImage,
    },
    {
      title: "Lara von Kolontaj",
      image: LaraVonKolontajImage,
    },
    {
      title: "Maja von Kutzschenbach",
      image: MajaVonKutzschenbachImage,
    },
    {
      title: "Melanie Treude",
      image: MelanieTreudeImage,
    },
    {
      title: "Meral Citak",
      image: MeralCitakImage,
    },
    {
      title: "Michael Bick",
      image: MichaelBickImage,
    },
    {
      title: "Sarah Longerich",
      image: SarahLongerichImage,
    },
    {
      title: "Sebastian Pleiß",
      image: SebastianPleissImage,
    },
    {
      title: "Sonja Hartmann",
      image: SonjaHartmannImage,
    },
    {
      title: "… und viele Weitere",
      subTitle: "sind jederzeit für Sie da.",
      image: WingImage,
      isLightVariant: true,
    },
  ];

  useEffect(() => {
    if (!showAllEmployees && toggleButtonRef.current) {
      toggleButtonRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [showAllEmployees]);

  return (
    <Section component={showHeadings ? "section" : "div"}>
      {showHeadings && (
        <Container variant={"xs"} showPaddingBlock={false} sx={{ paddingBottom: "3rem" }}>
          {!isSubsection && <Box sx={teamSectionStyle.scrollToAnchor} id="partneranwaelte" />}
          <Typography component={"h2"} variant={"h3"} align={"center"}>
            {!isSubsection && headline}
          </Typography>
          <Typography component={"h3"} variant={"h4"} align={"center"}>{isSubsection ? headline : subHeadline}</Typography>
        </Container>
      )}
      <Container variant={"xl"} showPaddingBlock={false}>
        <Grid container spacing={1} sx={{ justifyContent: "center" }}>
          {employees.map((employee: EmployeeItem, index: number) => (
            <Grid key={employee.title} sx={{ display: { xs: index < 3 || showAllEmployees ? "block" : "none", md: "block" } }}>
              <PictureTeaserCard image={employee.image} title={employee.title} subTitle={employee.subTitle} isLightVariant={employee.isLightVariant} />
            </Grid>
          ))}
        </Grid>
        <Stack ref={toggleButtonRef} direction="row" sx={teamSectionStyle.buttonContainer}>
          <Button onClick={() => setShowAllEmployees((prev) => !prev)} endIcon={showAllEmployees ? <ExpandLess /> : <ExpandMore />}>
            {showAllEmployees ? "Weniger anzeigen" : "Mehr anzeigen"}
          </Button>
        </Stack>
      </Container>
    </Section>
  );
};

export default TeamSection;
